import { useEffect, useState } from "react";
import ticketService from "../services/ticketService";
import useAuth from "../hooks/useAuth";
import TicketQrCard from "../Components/TicketQrCard";
import { Link } from "react-router-dom";
import { LuTicket, LuLogIn } from "react-icons/lu";

function Tickets() {
    const { user, isAuthenticated, loading: authLoading } = useAuth();
    const [tokenValid, setTokenValid] = useState(true);
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [reload, setReload] = useState(0);
    const userId = user?.id;

    useEffect(() => {
        const previousTitle = document.title;
        document.title = "Mis entradas | PassEvent";
        return () => { document.title = previousTitle; };
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        setTickets([]);
        setTokenValid(true);
        setLoadError(false);
        setLoading(Boolean(isAuthenticated && userId));
        async function fetchData() {
            if (!isAuthenticated || !userId) return;
            try {
                const response = await ticketService.getTicketsForUser(userId, controller.signal);
                if (!controller.signal.aborted) setTickets(response);
            } catch (error) {
                if (controller.signal.aborted) return;
                const status = error.response?.status;
                // Legacy session verification reports an invalid JWT as HTTP 500.
                const invalidSession = status === 500 && error.response?.data?.error === "Failed to authenticate token.";
                if (status === 401 || status === 403 || invalidSession) setTokenValid(false);
                else setLoadError(true);
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        }
        fetchData();
        return () => controller.abort();
    }, [isAuthenticated, userId, reload]);

    return (
        <div className="relative min-h-screen overflow-hidden bg-zinc-950">
            <div
                className="pointer-events-none absolute inset-0 opacity-70"
                aria-hidden="true"
                style={{
                    background:
                        "radial-gradient(circle at 15% 20%, rgba(255, 74, 110, 0.20), transparent 34%), radial-gradient(circle at 85% 15%, rgba(59, 130, 246, 0.14), transparent 28%), radial-gradient(circle at 50% 85%, rgba(250, 204, 21, 0.08), transparent 36%)",
                }}
            />
            <div className="relative mx-auto min-h-screen max-w-6xl px-4 pt-24">
                <header className="mb-8 text-center">
                    <h1 className="text-3xl font-bold tracking-wide text-white sm:text-4xl">Mis entradas</h1>
                </header>
                {authLoading || loading ? (
                    <div role="status" className="flex min-h-[58vh] items-center justify-center text-zinc-300">Cargando tus entradas…</div>
                ) : isAuthenticated && userId && tokenValid ? (
                    loadError ? (
                        <section className="mx-auto mt-10 flex min-h-[58vh] w-full max-w-3xl items-center justify-center">
                            <div className="w-full rounded-3xl border border-zinc-800 bg-zinc-900/65 p-8 text-center">
                                <h2 className="text-3xl font-bold text-white">No pudimos cargar tus entradas</h2>
                                <p role="alert" className="mt-3 text-zinc-300">Revisá tu conexión e intentá nuevamente.</p>
                                <button type="button" onClick={() => { setLoading(true); setReload((value) => value + 1); }}
                                    className="mt-7 cursor-pointer rounded-xl border border-zinc-700 bg-zinc-900 px-5 py-3 text-zinc-100 hover:border-zinc-500 hover:bg-zinc-800 active:bg-zinc-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-rose-300">
                                    Reintentar
                                </button>
                            </div>
                        </section>
                    ) : tickets.length > 0 ? (
                        tickets?.map((ticket) => {
                            return (
                                <TicketQrCard key={ticket.id} ticket={ticket} />
                            );
                        })
                    ) : (
                        <section className="mx-auto mt-10 flex min-h-[58vh] w-full max-w-3xl items-center justify-center">
                            <div className="w-full rounded-3xl border border-zinc-800 bg-zinc-900/65 p-8 text-center shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_20px_60px_rgba(0,0,0,0.35)] backdrop-blur-sm">
                                <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-zinc-800 text-rose-300">
                                    <LuTicket className="text-2xl" />
                                </div>
                                <h2 className="text-3xl font-bold text-white">
                                    Todavía no tenés tickets
                                </h2>
                                <p className="mx-auto mt-3 max-w-xl text-zinc-300">
                                    Cuando compres entradas, las vas a encontrar acá con su código QR listas para usar.
                                </p>
                                <div className="mt-7 flex justify-center">
                                    <Link
                                        to={"/"}
                                        className="rounded-xl bg-gradient-to-r from-rose-500 to-rose-600 px-5 py-2.5 font-medium text-white shadow-[0_10px_20px_rgba(244,63,94,0.25)] transition-transform hover:scale-[1.02]"
                                    >
                                        Explorar eventos
                                    </Link>
                                </div>
                            </div>
                        </section>
                    )
                ) : (
                    <section className="mx-auto mt-10 flex min-h-[58vh] w-full max-w-3xl items-center justify-center">
                        <div className="w-full rounded-3xl border border-zinc-800 bg-zinc-900/65 p-8 text-center shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_20px_60px_rgba(0,0,0,0.35)] backdrop-blur-sm">
                            <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-zinc-800 text-rose-300">
                                <LuLogIn className="text-2xl" />
                            </div>
                            <h2 className="text-3xl font-bold text-white">
                                Sesión expirada
                            </h2>
                            <p className="mx-auto mt-3 max-w-xl text-zinc-300">
                                Volvé a iniciar sesión para ver tus entradas y gestionar tus compras.
                            </p>
                            <div className="mt-7 flex justify-center gap-3">
                                <Link
                                    to={"/login"}
                                    className="rounded-xl bg-gradient-to-r from-rose-500 to-rose-600 px-5 py-2.5 font-medium text-white shadow-[0_10px_20px_rgba(244,63,94,0.25)] transition-transform hover:scale-[1.02]"
                                >
                                    Iniciar sesión
                                </Link>
                                <Link
                                    to={"/"}
                                    className="rounded-xl border border-zinc-700 bg-zinc-900/70 px-5 py-2.5 font-medium text-zinc-100 transition-colors hover:border-zinc-500 hover:bg-zinc-800"
                                >
                                    Ver eventos
                                </Link>
                            </div>
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}

export default Tickets;
